import type { EntryConditions, HistoryPoint, Timeframe } from "../types";
import { parseDbTimestamp } from "./format";

export type RsiZone = "buy" | "sell" | "neutral";
export type BbZone = "below" | "within" | "above";

export function rsiZone(rsi: number): RsiZone {
  if (rsi < 40) return "buy";
  if (rsi > 60) return "sell";
  return "neutral";
}

export function bbZone(price: number, upper: number, lower: number): BbZone {
  if (price < lower) return "below";
  if (price > upper) return "above";
  return "within";
}

export interface TimeframeReadout {
  timeframe: Timeframe;
  rsi: number;
  rsiZone: RsiZone;
  upper: number;
  middle: number;
  lower: number;
  bbZone: BbZone;
  // Agreement: does this timeframe match the BUY side, SELL side, or neither.
  buyAgrees: boolean;
  sellAgrees: boolean;
}

const TIMEFRAMES: Timeframe[] = ["1m", "5m", "15m", "30m"];

export function timeframeReadouts(ec: EntryConditions): TimeframeReadout[] {
  const price = ec.current_price;
  return TIMEFRAMES.map((tf) => {
    const rsi = ec[`rsi_${tf}`] as number;
    const upper = ec[`upper_bb_${tf}`] as number;
    const middle = ec[`middle_bb_${tf}`] as number;
    const lower = ec[`lower_bb_${tf}`] as number;
    const rz = rsiZone(rsi);
    const bz = bbZone(price, upper, lower);
    return {
      timeframe: tf,
      rsi,
      rsiZone: rz,
      upper,
      middle,
      lower,
      bbZone: bz,
      buyAgrees: rz === "buy" && bz === "below",
      sellAgrees: rz === "sell" && bz === "above",
    };
  });
}

export function normalizeSide(
  value: EntryConditions["initial_trade"]
): "BUY" | "SELL" | null {
  if (!value) return null;
  const s = String(value).toUpperCase();
  if (s.includes("BUY") || s.includes("LONG")) return "BUY";
  if (s.includes("SELL") || s.includes("SHORT")) return "SELL";
  return null;
}

export interface SignalEvent {
  /** Epoch ms of the bucket where the transition occurred. */
  time: number;
  kind: "initial" | "recovery";
  side: "BUY" | "SELL";
  price: number;
}

/**
 * Scan the history for initial/recovery trade transitions. A row only counts as
 * a new event when the side differs from the previous row's side on the same
 * kind — consecutive "BUY" entries collapse to one event. Recovery supersedes
 * initial: if both flip on the same bar, only the recovery event is emitted.
 */
export function extractSignalEvents(history: HistoryPoint[]): SignalEvent[] {
  const sorted = [...history].sort(
    (a, b) => parseDbTimestamp(a.bucket) - parseDbTimestamp(b.bucket)
  );
  const events: SignalEvent[] = [];
  let prevInit: "BUY" | "SELL" | null = null;
  let prevRec: "BUY" | "SELL" | null = null;
  for (const p of sorted) {
    const init = normalizeSide(p.initial_trade);
    const rec = normalizeSide(p.recovery_trade);
    const t = parseDbTimestamp(p.bucket);
    if (rec && rec !== prevRec) {
      events.push({ time: t, kind: "recovery", side: rec, price: p.current_price });
    } else if (!rec && init && init !== prevInit) {
      events.push({ time: t, kind: "initial", side: init, price: p.current_price });
    }
    prevInit = init;
    prevRec = rec;
  }
  return events;
}
